import styled from "@emotion/styled";
import { DateTimeFormatter, LocalDateTime } from "@js-joda/core";
import React from "react";
import {
  checkIfTimeSheetStrategy,
  getClockInStrategy,
  getClockOutStrategy,
} from "../clockInOutUtils";
import { VisitInstanceDetails, VisitInstanceId } from "../messages";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { CHECK_ICON } from "../utils/svgs_new";
import { AppButton } from "./Generic/AppButton";
import { If } from "./Generic/If";
import { isDefined } from "./Generic/IsDefined";
interface Props {
  visitDetails: VisitInstanceDetails;
  onClickClockIn: (visitDetails: VisitInstanceDetails) => void;
  onClickClockOut: (visitDetails: VisitInstanceDetails) => void;
  onClickSubmitTimeSheet: (visitInstanceId: VisitInstanceId) => void;
  onClickStartNow: (visitDetails: VisitInstanceDetails) => void;
  onClickFutureVisit: (visitDetails: VisitInstanceDetails) => void;
}

const VisitEngagementButtons = (props: Props) => {
  const { clockInTime, clockOutTime } = props.visitDetails;
  const clockInStrategy = getClockInStrategy(props.visitDetails);
  const clockOutStrategy = getClockOutStrategy(props.visitDetails);
  const isStartNowStrategy =
    clockInStrategy.visitType === "Present" || clockOutStrategy.visitType === "Present";
  const isTimesheetStrategy = checkIfTimeSheetStrategy(clockInStrategy, clockOutStrategy);

  const getEngagementFromVisit = (): {
    action: () => void;
    text: string;
    type: string;
    helperText?: string;
  } => {
    if (
      props.visitDetails.timeSheetSubmittedAt !== null &&
      props.visitDetails.timeSheetApprovedAt === null &&
      props.visitDetails.timeSheetDeclinedAt === null
    ) {
      return {
        action: () => props.onClickSubmitTimeSheet(props.visitDetails.id),
        text: `${translate(STRINGS.MANUALLY_SUBMITTED_INFO)}\n${translate(
          STRINGS.PENDING_APPROVAL
        )}`,
        type: "TimesheetSubmittedPendingApproval",
        helperText: `${translate(STRINGS.SUBMITTED_AT)}: ${LocalDateTime.ofInstant(
          props.visitDetails.timeSheetSubmittedAt
        ).format(DateTimeFormatter.ofPattern("MM/dd/yyyy HH:mm"))}`,
      };
    }

    if (
      props.visitDetails.timeSheetSubmittedAt !== null &&
      props.visitDetails.timeSheetApprovedAt !== null
    ) {
      return {
        action: () => props.onClickStartNow(props.visitDetails),
        text: `${translate(STRINGS.MANUALLY_SUBMITTED_INFO)}\n${translate(STRINGS.APPROVED)}`,
        type: "TimesheetSubmittedApproved",
        helperText: `${translate(STRINGS.SUBMITTED_AT)}: ${LocalDateTime.ofInstant(
          props.visitDetails.timeSheetSubmittedAt
        ).format(DateTimeFormatter.ofPattern("MM/dd/yyyy HH:mm"))}`,
      };
    }

    if (
      isDefined(clockInTime) &&
      !isDefined(clockOutTime) &&
      clockInStrategy.visitType === "Present"
    ) {
      return {
        action: () => props.onClickStartNow(props.visitDetails),
        text: `${translate(STRINGS.VISIT_STARTED_AT)} ${clockInTime.format(
          DateTimeFormatter.ofPattern("HH:mm")
        )}`,
        type: "ClockedIn",
      };
    }

    if (
      !isDefined(clockInTime) &&
      !isDefined(clockOutTime) &&
      clockInStrategy.visitType === "Present" &&
      props.visitDetails.dutySheetFillPercentage !== null &&
      props.visitDetails.dutySheetFillPercentage > 0
    ) {
      return {
        action: () => props.onClickStartNow(props.visitDetails),
        text: translate(STRINGS.VISIT_STARTED_MISSING_CLOCK_IN),
        type: "MissingClockIn",
      };
    }

    if (
      isDefined(clockInTime) &&
      isDefined(clockOutTime) &&
      !props.visitDetails.isDutySheetCompleted &&
      props.visitDetails.hasPlanOfCare
    ) {
      return {
        action: () => props.onClickStartNow(props.visitDetails),
        text: translate(STRINGS.SUBMIT_DUTIES),
        type: "IncompleteDuties",
      };
    }

    if (
      isDefined(clockInTime) &&
      isDefined(clockOutTime) &&
      (props.visitDetails.isDutySheetCompleted || !props.visitDetails.hasPlanOfCare)
    ) {
      return {
        action: () => props.onClickStartNow(props.visitDetails),
        text: translate(STRINGS.VISIT_COMPLETED_SUCCESSFULLY),
        type: "SuccessullyCompleted",
      };
    }

    if (
      isTimesheetStrategy ||
      clockInStrategy.visitType === "Past" ||
      clockOutStrategy.visitType === "Past"
    ) {
      return {
        action: () => props.onClickSubmitTimeSheet(props.visitDetails.id),
        text: translate(STRINGS.SUBMIT_DUTIES),
        type: "TimeSheet",
      };
    }

    if (isStartNowStrategy) {
      return {
        action: () => props.onClickStartNow(props.visitDetails),
        text: translate(STRINGS.START_NOW),
        type: "PresentVisit",
      };
    }

    return {
      action: () => props.onClickFutureVisit(props.visitDetails),
      text: translate(STRINGS.START_NOW),
      type: "FutureVisit",
    };
  };

  const engagement = getEngagementFromVisit();
  const timeSheetStatuses = ["TimesheetSubmittedPendingApproval", "TimesheetSubmittedApproved"];
  const checkStatuses = ["TimesheetSubmittedApproved", "SuccessullyCompleted"];
  return (
    <VisitEngagementButtons.Root type={engagement.type}>
      <AppButton
        variant={engagement.type === "FutureVisit" ? "soft" : "filled"}
        size="small"
        onClick={engagement.action}
      >
        <VisitEngagementButtons.ButtonText>
          {checkStatuses.includes(engagement.type) ? CHECK_ICON : null}
          {engagement.text}
        </VisitEngagementButtons.ButtonText>
      </AppButton>
      <If condition={timeSheetStatuses.includes(engagement.type)}>
        <VisitEngagementButtons.HelperText>
          {engagement.helperText}
        </VisitEngagementButtons.HelperText>
      </If>
    </VisitEngagementButtons.Root>
  );
};

VisitEngagementButtons.Root = styled.div<{ type: string }>`
  opacity: ${(prop) => (prop.type === "FutureVisit" ? 0.6 : 1)};
  white-space: break-spaces;
  display: flex;
  flex-direction: column;
  justify-content: center;
  .AppButton {
    margin-bottom: 5px;
    animation: ${(prop) => (prop.type === "PresentVisit" ? "pulse 2s ease infinite" : "none")};
  }
  width: 100%;
`;

VisitEngagementButtons.HelperText = styled.div`
  font-size: 14px;
  font-weight: 700;
  align-self: center;
`;

VisitEngagementButtons.ButtonText = styled.div`
  display: flex;
  gap: 5px;
  flex-direction: row;
`;

export default VisitEngagementButtons;
